import { useStore } from '@nanostores/react';
import { useMemo } from 'react';
import type { IconName } from '@customerio/pluma-icons';
import { useActiveBundle } from '@/context/active-bundle';
import { IconButton } from '@/layouts/authenticated/components/top-navigation/components/icon-button';
import { useCustomerFeedback } from '../use-customer-feedback';
import { feedbackButtonsAtom, type FeedbackButton } from '../registry';

/**
 * Renders every feedback button registered for the active app in a single consistent
 * location in the top navigation. Each button opens the shared feedback modal via
 * `useCustomerFeedback`, so all surfaces route through the same modal and endpoint.
 */
export function FeedbackButtons() {
  const registrations = useStore(feedbackButtonsAtom);
  const activeApp = useActiveBundle();
  const showFeedbackModal = useCustomerFeedback();

  const buttons = useMemo<FeedbackButton[]>(() => {
    const result: FeedbackButton[] = [];
    for (const [, entry] of registrations) {
      if (entry.app === activeApp) {
        result.push(entry);
      }
    }
    return result;
  }, [registrations, activeApp]);

  if (buttons.length === 0) {
    return null;
  }

  return (
    <>
      {buttons.map((button) => (
        <IconButton
          key={`${button.feature ?? 'general'}-${button.label}`}
          icon={(button.icon as IconName) ?? 'chat'}
          label={button.label}
          showLabel={true}
          size="md"
          onClick={() => {
            void showFeedbackModal({
              feature: button.feature,
              initialFeedback: button.initialFeedback,
              message: button.message,
            });
          }}
        />
      ))}
    </>
  );
}
